import { queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { LanguageDto } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useOptionalProjectContext } from "@/providers/ProjectContext";
import { QUERY_KEYS } from "@/query-keys";

export interface LanguageSubset {
  subset: "legacy" | "ui" | "autoTranslation";
}

export const useGeneralQueries = () => {
  const api = useApi();
  const project = useOptionalProjectContext();
  const projectId = project?.projectId;

  return {
    constraints: () => {
      if (!projectId) {
        throw new Error("Constraints require a project scope");
      }

      return queryOptions({
        queryKey: QUERY_KEYS.CONSTRAINTS(projectId),
        queryFn: () => api.getConstraintsV1(),
        select: commonAPIDataSelector,
        staleTime: 30 * 1000,
      });
    },

    intercomIdentity: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTERCOM_IDENTITY,
        queryFn: api.getIntercomIdentityV1,
        select: commonAPIDataSelector,
      }),

    languages: (options?: LanguageSubset) =>
      queryOptions({
        queryKey: QUERY_KEYS.LANGUAGES(options),
        queryFn: async () => {
          const data = await api.getLanguagesV2().then((x) => x.data);

          updateLocalStorage(LOCAL_STORAGE_KEYS.SUPPORTED_LANGUAGES, data);

          return data;
        },
        select: (data: LanguageDto[]) => {
          switch (options?.subset) {
            case "legacy": {
              const legacyLanguageIds: LanguageDto["id"][] = ["nl", "en", "dutch", "english"];

              return data.filter((currLanguage) => legacyLanguageIds.includes(currLanguage.id));
            }
            case "ui":
              return data.filter((currLanguage) => !currLanguage.autoTranslationLanguageOnly);
            // Auto translation languages include both the UI and auto translation only languages, so no need to filter
            case "autoTranslation":
            default:
              return data;
          }
        },
        initialData: () => {
          const value = getLocalStorageValue<LanguageDto[]>(LOCAL_STORAGE_KEYS.SUPPORTED_LANGUAGES, []);
          if (Array.isArray(value) && value.length > 0 && value[0].id) {
            return value;
          }
        },
        // Treat the localStorage-sourced initial data as stale so the query still
        // revalidates in the background (and rewrites AoP.languages) instead of
        // being considered fresh for 12h from every remount.
        initialDataUpdatedAt: 0,
        staleTime: 12 * 60 * 60 * 1000, // 12 hours
        notifyOnChangeProps: ["data", "isFetching", "error"],
      }),

    translations: (language: string, disableTranslations?: boolean) =>
      queryOptions({
        queryKey: QUERY_KEYS.TRANSLATIONS(language),
        queryFn: async () => {
          if (disableTranslations) {
            return {};
          }

          const result = await api.getTranslationsDetailsV1("dashboard", language as any);

          if (import.meta.env.DEV) {
            const devTranslations = await import("@/translations/english.json");
            const { parseTranslations } = await import("@/translations/implementation/parseTranslations");
            const devData = parseTranslations(devTranslations.default);
            result.data = { ...devData, ...result.data };
          }

          return result.data;
        },
      }),
  };
};
